import { Component, EventEmitter, Input, Output } from '@angular/core';

import { getQuotationStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { IQuotationDetail } from '../../models/quotations.models';
import { ISegregationCheck } from '../../services/segregation-preview.service';

/**
 * The tag strip that sits next to the order name in both order headers — the
 * order detail page and the side-by-side correction panel (WB-7938).
 *
 * Each surface used to hand-roll its own subset of these tags, so they drifted:
 * the status pill was 13px on one and 14px on the other, "Split applies" and
 * "Read-only" only ever rendered in the panel, and the duplicate badge only ever
 * on the detail page. One component now owns the set, the order and the geometry.
 *
 * Visibility stays with the tag, not the host: the only thing a host supplies is
 * the state it alone can know (the split proposal, whether a save is pending).
 */
@Component({
  selector: 'app-order-header-tags',
  templateUrl: './order-header-tags.component.html',
  styleUrls: ['./order-header-tags.component.scss'],
})
export class OrderHeaderTagsComponent {
  @Input() quotation: IQuotationDetail | undefined;

  /**
   * The split proposal currently attached to this order, or null. Owned by the
   * host because each surface sources it differently — `preview-for-partner` on
   * load, the details PATCH response after a save — and clears it after a split.
   */
  @Input() segregationCheck: ISegregationCheck | null = null;

  /**
   * Greys the split tag out and swallows its click. The side-by-side passes its
   * dirty flag: splitting would discard the unsaved edits.
   */
  @Input() splitBlocked = false;

  /** False while the host is still loading the order, or failed to load it. */
  @Input() ready = true;

  @Output() splitClick = new EventEmitter<void>();
  @Output() duplicateDismissed = new EventEmitter<void>();

  get status(): string {
    return this.quotation?.status ?? '';
  }

  get showStatus(): boolean {
    return this.ready && !!this.status;
  }

  /**
   * Records that have reached the ERP are read-only. Computed front-end only,
   * per WB-7478.
   */
  get isReadOnly(): boolean {
    return this.ready && ['done', 'sent_to_erp'].includes(this.status);
  }

  /**
   * WB-7569 — a splitting rule applies and can still be acted on. Suppressed on
   * read-only records: the split has nowhere to land once the order left for the
   * ERP.
   */
  get showSplit(): boolean {
    return this.ready
      && !!this.segregationCheck
      && this.isApprovableFromStatus
      && !this.quotation?.is_archived
      && !this.isReadOnly;
  }

  /**
   * WB-7938 — suppressed on read-only records alongside the split tag: there is
   * nothing left to do about a duplicate once the order reached the ERP.
   */
  get showDuplicate(): boolean {
    return this.ready
      && !!this.quotation?.duplicate_info?.has_duplicates
      && !this.isReadOnly;
  }

  getStatusStyles(): { [key: string]: string } {
    return getQuotationStatusStyles(this.status);
  }

  onSplitClick(): void {
    if (this.splitBlocked) return;
    this.splitClick.emit();
  }

  /** The only two statuses a split proposal can still be applied from. */
  private get isApprovableFromStatus(): boolean {
    return ['to_be_validated', 'pending_inventory'].includes(this.status);
  }
}
